import { ReactNode, useEffect, useRef, useState } from "react";
import { useTheme } from "../hooks/useTheme";

interface Tab {
    id?: number;
    label: string;
    icon?: ReactNode;
    width?: string;
}

interface TabsProps {
    tabs: Tab[];
    onChange?: (index: number, tab?: Tab) => void;
    activeIndex: number;
    width?: string;
}

export const Tabs = ({ tabs, onChange, activeIndex, width = "max-w-full" }: TabsProps) => {
    const { themeStyles } = useTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const overflowBtnRef = useRef<HTMLButtonElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const timerRef = useRef<number | null>(null);
    const lastKeyRef = useRef<string>(""); // remember last overflow key to avoid redundant updates

    const [overflowIndices, setOverflowIndices] = useState<number[]>([]);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current?.contains(event.target as Node) &&
                overflowBtnRef.current &&
                !overflowBtnRef.current?.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener("click", handleClickOutside);
        } else {
            document.removeEventListener("click", handleClickOutside);
        }

        return () => document.removeEventListener("click", handleClickOutside);
    }, [showDropdown]);

    const handleTabClick = (index: number, tab: Tab) => {
        onChange?.(index, tab);
    };

    const checkOverflow = () => {
        if (showDropdown) setShowDropdown(false);
        const container = containerRef.current;
        if (!container) return;

        const availableWidth = container?.clientWidth;
        if (!availableWidth) return;
        const reservedForHamburger = 80;

        let used = 0;
        const newOverflowIdx: number[] = [];

        for (let i = 0; i < tabs?.length; i++) {
            const el = tabRefs?.current[i];
            if (!el) break;
            used += el?.offsetWidth;
            if (used > (availableWidth - reservedForHamburger)) {
                newOverflowIdx.push(i);
            }
        }

        const key = newOverflowIdx.join(",");
        if (lastKeyRef.current !== key) {
            lastKeyRef.current = key;
            setOverflowIndices(newOverflowIdx);
            setIsOverflowing(newOverflowIdx?.length > 0);
        }
    };

    // Single scheduler used both for initial check and ResizeObserver callbacks.
    const scheduleCheck = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
        }
        timerRef.current = window.setTimeout(() => {
            // run just after paint
            requestAnimationFrame(() => {
                checkOverflow();
            });
        });
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const ro = new ResizeObserver(() => {
            scheduleCheck();
        });
        ro.observe(container);

        scheduleCheck(); // initial run

        return () => {
            ro.disconnect();
            if (timerRef.current) cancelAnimationFrame(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabs.length, activeIndex]);



    return (
        <div className="w-full relative">
            {/* Tabs Container */}
            <div ref={containerRef} className="w-full">
                <div className={`flex ${themeStyles.tabs.bg} px-[3px] py-[4px] rounded-lg gap-1.5 w-max overflow-hidden transition-all duration-300 ease-in-out`}>
                    {
                        tabs?.map((tab, i) => {
                            const isActive = `${i}-tab` === `${activeIndex}-tab`;
                            const isOverflowed = overflowIndices?.includes(i);

                            return (
                                <button
                                    key={i}
                                    ref={el => (tabRefs.current[i] = el)}
                                    onClick={() => handleTabClick(i, tab)}
                                    className={`flex items-center gap-2 px-[8px] py-[8px] rounded-md transition-all whitespace-nowrap ${isActive ? themeStyles.tabs.btnBg + " shadow-md" : ""} ${isOverflowed ? "absolute opacity-0 pointer-events-none" : ""}`} // keep ref, but hide
                                >
                                    {
                                        tab?.icon && (
                                            <span className={`material-symbols-outlined text-[25px] ${isActive ? themeStyles.tabs.textActiveTextColor : themeStyles.tabs.textInActiveTextColor}`}>
                                                {tab?.icon}
                                            </span>
                                        )
                                    }

                                    <span className={`text-[12px] font-medium ${isActive ? themeStyles.tabs.textActiveTextColor : themeStyles.tabs.textInActiveTextColor}`}>
                                        {tab?.label}
                                    </span>
                                </button>
                            );
                        })
                    }

                    {/* Overflow Menu Button */}
                    {
                        isOverflowing && (
                            <button
                                ref={overflowBtnRef}
                                onClick={() => setShowDropdown(!showDropdown)}
                                className={`flex items-center justify-between py-[5px] px-[6px] ${themeStyles.tabs.btnBg} gap-1.5 rounded-lg border border-solid ${themeStyles.main.borderColor}`}
                            >
                                <span className={`material-symbols-outlined text-[18px] font-light ${themeStyles.main.textHeadingColor}`}>
                                    menu
                                </span>
                            </button>
                        )
                    }
                </div>
            </div>

            {
                showDropdown && (
                    <div
                        ref={dropdownRef}
                        className={`${themeStyles.main.tailwindMainBg} absolute right-[20px] mt-2 rounded-lg shadow-lg border ${themeStyles.main.borderColor} z-[999]`}
                    >
                        <div className="flex flex-col min-w-[150px] max-h-[300px] overflow-y-auto">
                            {
                                overflowIndices?.map((idx) => {
                                    const tab = tabs[idx];
                                    const isActive = `${idx}-menu-tab` === `${activeIndex}-menu-tab`;
                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => handleTabClick(idx, tab)}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-md text-left ${isActive ? themeStyles.tabs.btnBg : "hover:bg-gray-100"}`}
                                        >
                                            {
                                                tab?.icon && (
                                                    <span className={`material-symbols-outlined ${isActive ? themeStyles.tabs.textActiveTextColor : themeStyles.tabs.textInActiveTextColor}`}>
                                                        {tab?.icon}
                                                    </span>
                                                )
                                            }

                                            <span className={`text-sm ${isActive ? themeStyles.tabs.textActiveTextColor : themeStyles.tabs.textInActiveTextColor}`}>
                                                {tab.label}
                                            </span>
                                        </button>
                                    );
                                })
                            }
                        </div>
                    </div>
                )
            }
        </div>
    );
};
